import React from 'react';
import ReactFlow, {
  MiniMap,
  Controls,
  Background,
  Handle,
  Position,
  BaseEdge,
  EdgeLabelRenderer,
  getBezierPath,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { Badge, Box, Button, Typography, Stack, Paper, IconButton, Tooltip } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import { formatText } from '../common/TextFormatterProvider';

function DataSourceNode({ data }) {
  return (
    <Paper elevation={3} sx={{
      p:1,
      border: '1px solid rgba(255, 255, 255, 0.08)',
      minWidth: 150,
      backgroundColor: '#2EB998',
      color: '#F0F2F5',
      borderRadius: 2
    }}>
      <Typography variant="body2" align="center" sx={{ fontWeight: 'bold' }}>
        {formatText(data.config?.name) || 'Data Source Block'}
      </Typography>
      <Typography variant="caption" display="block" align="center">
        (DataSource)
      </Typography>
      <Stack direction="row" justifyContent="center" spacing={1} sx={{ mt:1, position: 'relative' }}>
        <Handle type="source" position={Position.Right} isConnectable={true} />
        <Button 
          size="small" 
          onClick={() => data.onConfigure(data.id)} 
          sx={{ 
            bgcolor: 'rgba(0, 0, 0, 0.2)', 
            color: '#F0F2F5', 
            '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.3)' },
            fontSize: '0.7rem',
            py: 0.5,
            px: 1
          }}>
          Configure
        </Button>
        <IconButton 
          size="small" 
          onClick={() => data.onDelete(data.id)}
          sx={{ 
            color: '#F0F2F5', 
            p: 0.5,
            '&:hover': { color: '#FF5252', bgcolor: 'rgba(0, 0, 0, 0.1)' },
          }}>
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Stack>
    </Paper>
  );
}

function LogicNode({ data }) {
  return (
    <Paper elevation={3} sx={{
      p:1,
      border: '1px solid rgba(255, 255, 255, 0.08)',
      minWidth: 150,
      backgroundColor: '#7E58B5',
      color: '#F0F2F5',
      borderRadius: '50%'
    }}>
      <Typography variant="body2" align="center" sx={{ fontWeight: 'bold' }}>
        {formatText(data.config?.name) || 'Logic Block'}
      </Typography>
      <Typography variant="caption" display="block" align="center">
        (Logic)
      </Typography>
      {data.repairReason && (
        <Tooltip title={data.repairReason} arrow>
          <Typography variant="caption" display="block" align="center" sx={{ fontWeight: 'bold' }}>
            Needs source repair
          </Typography>
        </Tooltip>
      )}
      <Stack direction="row" justifyContent="center" spacing={1} sx={{ mt:1, position: 'relative' }}>
        <Handle type="target" position={Position.Left} isConnectable={true} />
        <Handle type="source" position={Position.Right} isConnectable={true} />
        <Button 
          size="small" 
          onClick={() => data.onConfigure(data.id)}
          sx={{ 
            bgcolor: 'rgba(0, 0, 0, 0.2)', 
            color: '#F0F2F5', 
            '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.3)' },
            fontSize: '0.7rem',
            py: 0.5,
            px: 1
          }}>
          Configure
        </Button>
        <IconButton 
          size="small" 
          onClick={() => data.onDelete(data.id)}
          sx={{ 
            color: '#F0F2F5', 
            p: 0.5,
            '&:hover': { color: '#FF5252', bgcolor: 'rgba(0, 0, 0, 0.1)' },
          }}>
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Stack>
    </Paper>
  );
}

function OutputNode({ data }) {
  const bindingErrors = data.bindingErrors || [];

  return (
    <Tooltip
      arrow
      placement="top"
      title={bindingErrors.map((bindingError) => bindingError.message).join('\n')}
      disableHoverListener={bindingErrors.length === 0}
    >
      <Badge
        color="error"
        badgeContent={bindingErrors.length}
        invisible={bindingErrors.length === 0}
      >
        <Paper elevation={3} sx={{
          p:1,
          border: bindingErrors.length > 0
            ? '2px solid #FF5252'
            : '1px solid rgba(255, 255, 255, 0.08)',
          minWidth: 150,
          backgroundColor: '#F0B25A',
          color: '#111318',
          borderRadius: 0
        }}>
          <Typography variant="body2" align="center" sx={{ fontWeight: 'bold' }}>
            {formatText(data.config?.name) || 'Output Block'}
          </Typography>
          <Typography variant="caption" display="block" align="center">
            (Output)
          </Typography>
          <Stack direction="row" justifyContent="center" spacing={1} sx={{ mt:1, position: 'relative' }}>
            <Handle type="target" position={Position.Left} isConnectable={true} />
            <Button
              size="small"
              onClick={() => data.onConfigure(data.id)}
              sx={{
                bgcolor: 'rgba(0, 0, 0, 0.2)',
                color: '#111318',
                '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.3)' },
                fontSize: '0.7rem',
                py: 0.5,
                px: 1
              }}>
              Configure
            </Button>
            <IconButton
              size="small"
              onClick={() => data.onDelete(data.id)}
              sx={{
                color: '#111318',
                p: 0.5,
                '&:hover': { color: '#FF5252', bgcolor: 'rgba(0, 0, 0, 0.1)' },
              }}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Paper>
      </Badge>
    </Tooltip>
  );
}

function PredefinedNode({ data }) {
  return (
    <Paper elevation={3} sx={{
      p:1,
      border: '1px solid rgba(255, 255, 255, 0.08)',
      minWidth: 150,
      backgroundColor: '#FF6F61',
      color: '#F0F2F5',
      borderRadius: 1
    }}>
      <Typography variant="body2" align="center" sx={{ fontWeight: 'bold' }}>
        {formatText(data.config?.name) || 'Predefined Block'}
      </Typography>
      <Typography variant="caption" display="block" align="center">
        (Predefined)
      </Typography>
      <Stack direction="row" justifyContent="center" spacing={1} sx={{ mt:1, position: 'relative' }}>
        <Handle type="target" position={Position.Left} isConnectable={true} />
        <Handle type="source" position={Position.Right} isConnectable={true} />
        <Button 
          size="small" 
          onClick={() => data.onConfigure(data.id)}
          sx={{ 
            bgcolor: 'rgba(0, 0, 0, 0.2)', 
            color: '#F0F2F5', 
            '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.3)' },
            fontSize: '0.7rem',
            py: 0.5,
            px: 1
          }}>
          Configure
        </Button>
        <IconButton 
          size="small" 
          onClick={() => data.onDelete(data.id)}
          sx={{ 
            color: '#F0F2F5', 
            p: 0.5,
            '&:hover': { color: '#FF5252', bgcolor: 'rgba(0, 0, 0, 0.1)' },
          }}>
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Stack>
    </Paper>
  );
}

const nodeTypes = {
  dataSourceNode: DataSourceNode,
  logicNode: LogicNode,
  outputNode: OutputNode,
  predefinedNode: PredefinedNode
};

// Every connection carries a small × at its midpoint: the one way to remove a
// connection on its own. Keyboard deletion stays off (deleteKeyCode below) so
// a stray Backspace cannot remove a block, and removing the block was the only
// other way to lose an edge. `data.onRemove` is attached by FlowCanvas.
function RemovableEdge({
  id, sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition, style, markerEnd, data,
}) {
  const [path, labelX, labelY] = getBezierPath({
    sourceX, sourceY, sourcePosition, targetX, targetY, targetPosition,
  });
  return (
    <>
      <BaseEdge id={id} path={path} style={style} markerEnd={markerEnd} />
      <EdgeLabelRenderer>
        <Tooltip title="Remove connection">
          <IconButton
            size="small"
            aria-label="Remove connection"
            className="nodrag nopan"
            onClick={(event) => {
              event.stopPropagation();
              data?.onRemove?.(id);
            }}
            sx={{
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
              pointerEvents: 'all',
              width: 20,
              height: 20,
              p: 0,
              bgcolor: '#1A1E26',
              border: '1px solid rgba(255, 255, 255, 0.25)',
              color: 'rgba(255, 255, 255, 0.7)',
              opacity: 0.75,
              '&:hover': { opacity: 1, color: '#FF5252', borderColor: '#FF5252', bgcolor: '#1A1E26' },
            }}
          >
            <CloseIcon sx={{ fontSize: 12 }} />
          </IconButton>
        </Tooltip>
      </EdgeLabelRenderer>
    </>
  );
}

const edgeTypes = { default: RemovableEdge };

function FlowCanvas({
  nodes,
  edges,
  onNodesChange,
  onEdgesChange,
  onConnect,
  isValidConnection,
  onConfigureNode,
  onDeleteNode,
  outputBindingErrors = {},
  logicRepairReasons = {},
}) {
  // Removing a connection goes through the same change handler React Flow
  // uses, so it is committed — and undone on failure — like any other removal.
  const removeEdge = (edgeId) => onEdgesChange([{ id: edgeId, type: 'remove' }]);
  const enhancedEdges = edges.map((edge) => ({
    ...edge,
    data: { ...(edge.data || {}), onRemove: removeEdge },
  }));
  const enhancedNodes = nodes.map(n => ({
    ...n,
    data: {
      ...n.data,
      id: n.id,
      onConfigure: onConfigureNode,
      onDelete: onDeleteNode,
      bindingErrors: outputBindingErrors[n.id] || [],
      repairReason: logicRepairReasons[n.id] || null
    }
  }));

  return (
    <Box sx={{ width: '100%', height: '100%' }}>
      <ReactFlow
        nodes={enhancedNodes}
        edges={enhancedEdges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        isValidConnection={isValidConnection}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        deleteKeyCode={null}
        fitView
      >
        <MiniMap />
        <Controls />
        <Background />
      </ReactFlow>
    </Box>
  );
}

export default FlowCanvas;
